import { useInView } from '../../../hooks/useInView';

const plans = [
  {
    name: 'ライトプラン',
    price: '49,800',
    unit: '円/月',
    tag: null,
    desc: '採用担当者が初めてスカウト代行を試したい企業に',
    highlight: false,
    features: [
      { text: '月間スカウト送信数 最大250通', available: true },
      { text: 'ターゲット設計・条件設定', available: true },
      { text: 'スカウト文面作成（共通テンプレート）', available: true },
      { text: '月次レポート提供', available: true },
      { text: '文面カスタマイズ（個別最適化）', available: false },
      { text: '専任担当者サポート', available: false },
    ],
    cta: 'まずは相談する',
    ctaStyle: 'border border-[#1a2e4a] text-[#1a2e4a] hover:bg-[#1a2e4a] hover:text-white',
  },
  {
    name: 'スタンダードプラン',
    price: '89,800',
    unit: '円/月',
    tag: '最も人気',
    desc: '本格的な母集団形成を目指す採用担当者向けの標準プラン',
    highlight: true,
    features: [
      { text: '月間スカウト送信数 最大500通', available: true },
      { text: 'ターゲット設計・条件設定', available: true },
      { text: 'スカウト文面作成（共通テンプレート）', available: true },
      { text: '月次レポート提供', available: true },
      { text: '文面カスタマイズ（個別最適化）', available: true },
      { text: '専任担当者サポート', available: false },
    ],
    cta: 'このプランで相談する',
    ctaStyle: 'bg-[#E8601C] text-white hover:bg-[#d55519]',
  },
  {
    name: 'プレミアムプラン',
    price: '149,800',
    unit: '円/月',
    tag: null,
    desc: '採用戦略から一貫して伴走するフルサポートプラン',
    highlight: false,
    features: [
      { text: '月間スカウト送信数 最大1000通', available: true },
      { text: 'ターゲット設計・条件設定', available: true },
      { text: 'スカウト文面作成（共通テンプレート）', available: true },
      { text: '月次レポート提供', available: true },
      { text: '文面カスタマイズ（個別最適化）', available: true },
      { text: '専任担当者サポート', available: true },
    ],
    cta: 'まずは相談する',
    ctaStyle: 'border border-[#1a2e4a] text-[#1a2e4a] hover:bg-[#1a2e4a] hover:text-white',
  },
];

export default function PricingSection() {
  const { ref, inView } = useInView();

  const handleNav = () => {
    const target = document.querySelector('#contact');
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" className="py-10 md:py-20 bg-[#f7f5f2]">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          className={`text-center mb-8 md:mb-14 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="text-xs tracking-widest text-[#E8601C] font-medium uppercase mb-3 block">
            PRICING
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2e4a] leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            料金プラン
          </h2>
          <p className="text-[#666] text-sm mt-4 leading-relaxed">
            企業規模や採用目標に合わせて選べる3つのプラン。<br className="hidden md:block" />
            すべて初期費用なし、月額制でスタートできます。
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 items-start">
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl flex flex-col transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${
                plan.highlight
                  ? 'bg-[#1a2e4a] text-white ring-2 ring-[#E8601C]'
                  : 'bg-white border border-gray-100'
              }`}
              style={{ transitionDelay: inView ? `${i * 150 + 200}ms` : '0ms' }}
            >
              {/* Popular Badge */}
              {plan.tag && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#E8601C] text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
                    {plan.tag}
                  </span>
                </div>
              )}

              <div className="p-6 sm:p-8 flex flex-col flex-1">
                {/* Plan Name */}
                <p
                  className={`text-sm font-bold mb-1 ${plan.highlight ? 'text-white/70' : 'text-[#999]'}`}
                >
                  {plan.name}
                </p>

                {/* Price */}
                <div className="flex items-end gap-1 mb-3">
                  <span
                    className={`text-3xl sm:text-4xl font-bold ${plan.highlight ? 'text-white' : 'text-[#1a2e4a]'}`}
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    ¥{plan.price}
                  </span>
                  <span className={`text-sm mb-1 ${plan.highlight ? 'text-white/60' : 'text-[#999]'}`}>
                    {plan.unit}
                  </span>
                </div>

                <p className={`text-xs leading-relaxed mb-6 ${plan.highlight ? 'text-white/70' : 'text-[#666]'}`}>
                  {plan.desc}
                </p>

                {/* Divider */}
                <div className={`h-px mb-6 ${plan.highlight ? 'bg-white/10' : 'bg-gray-100'}`} />

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f) => (
                    <li key={f.text} className="flex items-start gap-2.5">
                      <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 ${f.available ? '' : 'opacity-30'}`}>
                        <i
                          className={`text-sm ${
                            f.available
                              ? plan.highlight
                                ? 'ri-check-line text-[#E8601C]'
                                : 'ri-check-line text-[#1a2e4a]'
                              : 'ri-close-line text-[#999]'
                          }`}
                        />
                      </div>
                      <span
                        className={`text-xs leading-relaxed ${
                          f.available
                            ? plan.highlight
                              ? 'text-white/80'
                              : 'text-[#444]'
                            : plan.highlight
                            ? 'text-white/30'
                            : 'text-[#bbb]'
                        }`}
                      >
                        {f.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={handleNav}
                  className={`w-full text-sm font-medium py-3 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap ${plan.ctaStyle}`}
                >
                  {plan.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div
          className={`mt-8 sm:mt-10 text-center transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: inView ? '700ms' : '0ms' }}
        >
          <p className="text-xs text-[#999] leading-relaxed">
            ※ 表示は税抜価格です。初期費用・解約違約金は一切ありません。<br className="hidden sm:block" />
            ご予算や状況によってカスタマイズプランもご相談承ります。
          </p>
          <button
            onClick={handleNav}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#E8601C] hover:underline cursor-pointer"
          >
            <i className="ri-arrow-right-circle-line" />
            プランについて詳しく相談する
          </button>
        </div>
      </div>
    </section>
  );
}
